<template>
  <div
      ref="root"
      class="base-mobile-navigation"
  >
    <div
        class="aside-item-wrapper"
        :class="{
          'open': asideItemOpen
        }"
        :style="{
          '--item-transition-duration': `${asideItemTransitionDuration / 1000}s`
        }"
    >
      <div
          v-if="selectedAsideItem"
          class="container-fluid"
      >
        <div
            class="aside-item-content"
            v-html="selectedAsideItem.content"
        />
        <div
            class="aside-item-close"
            @click="closeAsideItem"
        >
          <FontAwesomeIcon icon="fa-regular fa-xmark" />
        </div>
      </div>
    </div>
    <div
        ref="waveEl"
        class="base-mobile-navigation__wave"
    >
      <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 1921 273.4" style="enable-background:new 0 0 1921 273.4;" xml:space="preserve">
      <path d="M0,0l1921,0v83.4c-144-47.7-360.3-73.9-609.1-73.9c-110.5,0-225.4,5.2-341.7,15.3C865.3,34,761.8,47.1,662.7,63.7C566.6,79.7,475,99,390.5,120.9C222.9,164.5,87.9,217.2,0,273.4L0,0z" fill="#fff"/>
    </svg>
    </div>
    <div class="base-mobile-navigation__content">
      <div class="container-fluid">
        <div class="aside-items">
          <BaseFixedAsideElementMobileToggle
              v-for="(item, inx) in AsideItems"
              :key="`mobile-aside-item-${inx}`"
              :style="{
                gridColumn: `${inx + 1} / ${inx + 2}`,
              }"
              :active="selectedAsideItemInx === inx"
              v-bind="item"
              @click="toggleAsideItem(inx)"
          />
        </div>
        <button
            class="sidebar-toggle"
            @click="toggleSidebar"
        >
          <FontAwesomeIcon icon="fa-light fa-bars" />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseFixedAsideElementMobileToggle from '@/components/aside/BaseFixedAsideElementMobileToggle.vue';
import {
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faUserCircle,
  faSearch,
} from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import AsideItems, { AsideItem } from '@/data/AsideItems';
import { useRoute } from 'vue-router';

library.add(faUserCircle, faSearch, faXmark);

const store = useStore();
const toggleSidebar = () => {
  store.dispatch('sidebar/toggle', 'sidebar');
};

const route = useRoute();
const asideItemOpen = ref<boolean>(false);
const selectedAsideItem = ref<AsideItem>();
const selectedAsideItemInx = ref<number>();
const asideItemTransitionDuration = 475;

const closeAsideItem = () => {
  asideItemOpen.value = false;

  setTimeout(() => {
    selectedAsideItem.value = undefined;
    selectedAsideItemInx.value = undefined;
  }, asideItemTransitionDuration);
};
const selectAsideItem = (inx: number) => {
  if (selectedAsideItemInx.value === inx) {
    closeAsideItem();

    return;
  }

  selectedAsideItem.value = AsideItems[inx];
  if (selectedAsideItem.value === undefined) return;

  store.dispatch('sidebar/close');
  selectedAsideItemInx.value = inx;
  asideItemOpen.value = true;
};
const toggleAsideItem = (inx: number) => {
  const selectedItem = AsideItems[inx];

  if (selectedItem.link) {
    closeAsideItem();

    return;
  }

  if (selectedAsideItem.value !== undefined) {
    asideItemOpen.value = false;

    setTimeout(() => {
      selectAsideItem(inx);
    }, asideItemTransitionDuration);

    return;
  }

  selectAsideItem(inx);
};
watch(() => route.fullPath, closeAsideItem);

const root = ref<HTMLElement>();
const waveEl = ref<HTMLElement>();
const setBodyStyle = (padding: number | undefined = undefined) => {
  const bodyEl = document.querySelector('body');

  if (!bodyEl) return;
  if (padding !== undefined) {
    bodyEl.style.paddingBottom = `${padding}px`;

    return;
  }

  if (!root.value) return;
  if (!waveEl.value) return;

  bodyEl.style.paddingBottom = `${root.value.offsetHeight + waveEl.value.offsetHeight}px`;
};
const setBodyPaddingBottom = () => {
  setBodyStyle();
};

onMounted(() => {
  window.addEventListener('resize', setBodyPaddingBottom);
  window.addEventListener('load', setBodyPaddingBottom);
  window.addEventListener('app.sidebar.open', closeAsideItem);
  setBodyPaddingBottom();
  for (let i = 100; i < 1000; i += 100) {
    setTimeout(setBodyPaddingBottom, i);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setBodyPaddingBottom);
  window.removeEventListener('load', setBodyPaddingBottom);
  window.removeEventListener('app.sidebar.open', closeAsideItem);
  setBodyStyle(0);
});
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.base-mobile-navigation {
  background-color: #FFFFFF;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1002;

  &__wave {
    position: absolute;
    bottom: calc(100% - 1px);
    width: 100%;
    left: 0;
    pointer-events: none;

    svg {
      display: block;
      width: 100%;
      height: auto;
      transform: rotate(180deg);
      filter: drop-shadow(0 0 5px rgba(0,0,0,.1607843137));
    }
  }

  &__content {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      inset: 0;
      background-color: #FFFFFF;
      z-index: 0;
    }

    > .container-fluid {
      position: relative;
      z-index: 1;
      height: 4.6875rem;
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;

      .aside-items {
        grid-column: 1 / 2;
        display: grid;
        grid-auto-columns: auto;
        grid-template-rows: auto;
        grid-gap: 20px;
      }

      .sidebar-toggle {
        grid-column: 3 / 4;
        background-color: transparent;
        font-size: 2rem;
        padding: 10px;
        margin: 0;
        border: none;
      }
    }
  }

  .aside-item-wrapper {
    position: absolute;
    bottom: 100%;
    left: 0;
    width: 100%;
    background-color: #E7F1F5;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    padding-bottom: 10.25641vw;
    border-radius: 20px 20px 0 0;
    transform: translateY(100%);
    transition: transform var(--item-transition-duration) ease-out;

    &.open {
      transform: translateY(0);
    }

    > .container-fluid {
      display: grid;
      grid-template-columns: 1fr auto;
    }

    .aside-item-content {
      font-size: .875rem;
      @include font-weight-fix;
      line-height: 1.25;
      padding-top: 20px;

      a {
        text-decoration: none;
        color: #293033;
      }
    }

    .aside-item-close {
      line-height: 1;
      padding: 20px 0 20px 20px;
      cursor: pointer;
    }
  }
}
</style>
